import * as THREE from 'three';
import { box, cyl, M, mat, mesh, registerNightGlow, texMat, worldBox } from '../materials';
import {
  corkBoardTexture,
  roofTexture,
  sidingTexture,
  signTexture,
  woodTexture,
  type SignSpec,
} from '../textures';
import { iceChest } from './props';
import { buildTrailer } from './trailer';

/** Buildings are built with their storefront facing +z. */

let storeGlass: THREE.MeshStandardMaterial | null = null;
function litStoreGlass(): THREE.MeshStandardMaterial {
  if (!storeGlass) {
    storeGlass = new THREE.MeshStandardMaterial({
      color: '#a9c8d8',
      roughness: 0.15,
      metalness: 0.3,
      emissive: new THREE.Color('#fff0c8'),
      emissiveIntensity: 0.15,
    });
    registerNightGlow(storeGlass, 1.3);
  }
  return storeGlass;
}

function signPlane(spec: SignSpec, w: number, h: number, glow = 0): THREE.Mesh {
  const tex = signTexture(spec);
  const m = texMat(tex, glow > 0 ? { emissive: '#ffffff', emissiveIntensity: 0 } : {});
  if (glow > 0) registerNightGlow(m, glow);
  return mesh(new THREE.PlaneGeometry(w, h), m, 0, 0, 0, { cast: false });
}

function poster(text: string, bg: string, fg: string, w = 1.1, h = 0.8): THREE.Mesh {
  // Canvas matches the poster's shape so the lettering isn't stretched.
  const ch = Math.round((256 * h) / w);
  return signPlane(
    {
      w: 256,
      h: ch,
      bg,
      fg,
      lines: [{ text, size: Math.min(54, Math.round(ch * 0.6)), font: 'Anton' }],
      border: fg,
      borderWidth: 6,
    },
    w,
    h,
    0.6,
  );
}

function storeShell(w: number, d: number, h: number, wall: string): THREE.Group {
  const g = new THREE.Group();
  g.add(mesh(worldBox(w, h, d, 2), texMat(sidingTexture(), { color: wall }), 0, h / 2, 0));
  g.add(box(w + 0.4, 0.3, d + 0.4, mat('#5d5a55'), 0, h + 0.15, 0));
  g.add(box(w + 0.1, 0.25, d + 0.1, M.cinder, 0, 0.12, 0, { cast: false }));
  return g;
}

export function gasStation(): THREE.Group {
  const g = storeShell(16, 10, 4.2, '#efe3c6');
  const front = 5.02;
  const band = box(16.3, 1.1, 0.35, mat('#c62828'), 0, 3.75, front + 0.1);
  g.add(band);
  const logo = signPlane(
    {
      w: 1152,
      h: 128,
      bg: '#c62828',
      fg: '#ffe14d',
      lines: [{ text: 'GAS-N-GRUB', size: 100, font: 'Bangers' }],
    },
    9,
    1.0,
    1.4,
  );
  logo.position.set(0, 3.75, front + 0.29);
  g.add(logo);
  const glass = mesh(new THREE.PlaneGeometry(13, 2.3), litStoreGlass(), 0, 1.6, front + 0.01, {
    cast: false,
  });
  g.add(glass);
  g.add(box(1.6, 2.4, 0.08, M.steel, 0, 1.2, front + 0.04, { cast: false }));
  g.add(mesh(new THREE.PlaneGeometry(1.4, 2.2), litStoreGlass(), 0, 1.15, front + 0.09, { cast: false }));
  const posters: Array<[string, string, string, number]> = [
    ['COLD BEER', '#1d4e9e', '#ffffff', -5.4],
    ['LOTTO', '#ffd400', '#c62828', -3.6],
    ['LIVE BAIT', '#2f6b3a', '#ffffff', -2.0],
    ['HOT DOGS 2/$3', '#ffffff', '#c62828', 2.0],
    ['ATM INSIDE', '#111111', '#7ed957', 3.7],
    ['NO SHIRT? NO PROBLEM', '#ff6a00', '#111111', 5.5],
  ];
  for (const [t, bg, fg, x] of posters) {
    const p = poster(t, bg, fg);
    p.position.set(x, 1.7, front + 0.03);
    g.add(p);
  }
  const ice = iceChest();
  ice.position.set(3.6 + 0.5, 0, front + 0.8);
  g.add(ice);
  g.add(box(1.2, 1.6, 0.8, mat('#9aa0a6', { metal: 0.4 }), -6.6, 0.8, front + 0.6));
  return g;
}

export function gasCanopy(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(14, 0.6, 9, mat('#f4f4f4'), 0, 4.8, 0));
  g.add(box(14.1, 0.45, 9.1, mat('#1f6fd1'), 0, 4.8, 0, { cast: false }));
  const under = new THREE.MeshStandardMaterial({
    color: '#ffffff',
    emissive: new THREE.Color('#fff6dc'),
    emissiveIntensity: 0,
  });
  registerNightGlow(under, 1.6);
  const panel = mesh(new THREE.PlaneGeometry(12, 7.5), under, 0, 4.49, 0, { cast: false });
  panel.rotation.x = Math.PI / 2;
  g.add(panel);
  for (const x of [-6, 6])
    for (const z of [-3.5, 3.5]) g.add(box(0.45, 4.5, 0.45, mat('#e8e8e8'), x, 2.25, z));
  return g;
}

export function priceSign(): THREE.Group {
  const g = new THREE.Group();
  g.add(cyl(0.2, 0.25, 7, M.steel, 0, 3.5, 0, 8));
  const face = signPlane(
    {
      w: 512,
      h: 640,
      bg: '#fdfaf0',
      fg: '#111',
      border: '#c62828',
      borderWidth: 18,
      gap: 14,
      lines: [
        { text: 'GAS-N-GRUB', size: 86, font: 'Bangers', color: '#c62828' },
        { text: 'UNLEADED  3.49⁹', size: 62, font: 'Anton' },
        { text: 'DIESEL  3.89⁹', size: 62, font: 'Anton' },
        { text: 'COLD BEER', size: 58, font: 'Anton', color: '#1d4e9e' },
        { text: 'LIVE BAIT', size: 58, font: 'Anton', color: '#2f6b3a' },
        { text: 'PRAY FOR RAIN', size: 46, font: 'Permanent Marker', color: '#555' },
      ],
    },
    3.2,
    4,
    0.9,
  );
  face.position.set(0, 8.6, 0.12);
  g.add(face);
  g.add(box(3.3, 4.1, 0.2, mat('#c62828'), 0, 8.6, 0));
  return g;
}

export function laundromat(): THREE.Group {
  const g = storeShell(16, 10, 4, '#cfe3ee');
  const front = 5.02;
  const logo = signPlane(
    {
      w: 1024,
      h: 130,
      bg: '#1f6fb5',
      fg: '#ffffff',
      lines: [{ text: 'SUDS-N-DUDS  24HR LAUNDRY', size: 92, font: 'Bangers' }],
    },
    11,
    1.4,
    1.3,
  );
  logo.position.set(0, 3.4, front + 0.06);
  g.add(logo);
  // Built facing +z and rotated 180° in the world, so local -x ends up on the east side.
  const glass = mesh(new THREE.PlaneGeometry(5, 2.1), litStoreGlass(), -4.6, 1.5, front + 0.01, {
    cast: false,
  });
  g.add(glass);
  for (let i = 0; i < 4; i++) {
    const w = mesh(
      new THREE.CircleGeometry(0.3, 14),
      mat('#3b4a55', { rough: 0.2 }),
      -6.4 + i * 1.2,
      1.0,
      front - 0.1,
      { cast: false },
    );
    g.add(w);
  }
  g.add(box(1.5, 2.4, 0.08, M.steel, -0.8, 1.2, front + 0.04, { cast: false }));
  g.add(mesh(new THREE.PlaneGeometry(1.3, 2.2), litStoreGlass(), -0.8, 1.15, front + 0.09, { cast: false }));
  const board = mesh(new THREE.PlaneGeometry(2.4, 1.5), texMat(corkBoardTexture()), 5.5, 1.7, front + 0.045, {
    cast: false,
  });
  g.add(board);
  g.add(box(2.6, 1.7, 0.06, mat('#5a3a1e'), 5.5, 1.7, front + 0.0));
  const p = poster('NOT RESPONSIBLE FOR LOST SOCKS', '#ffffff', '#1f6fb5', 1.6, 0.6);
  p.position.set(2.2, 2.2, front + 0.03);
  g.add(p);
  return g;
}

export function pawnShop(): THREE.Group {
  const g = storeShell(14, 10, 4.2, '#d9b45a');
  const front = 5.02;
  const logo = signPlane(
    {
      w: 1024,
      h: 200,
      bg: '#2b2b2b',
      fg: '#ffd400',
      gap: 4,
      lines: [
        { text: "LUCKY'S PAWN & PAYDAY", size: 92, font: 'Rye' },
        { text: 'WE BUY GOLD • CHECKS CASHED • NO QUESTIONS', size: 40, font: 'Anton', color: '#ffffff' },
      ],
    },
    11,
    2.1,
    1.2,
  );
  logo.position.set(0, 3.5, front + 0.06);
  g.add(logo);
  for (const x of [-4, 3.5]) {
    const glass = mesh(new THREE.PlaneGeometry(3, 1.8), litStoreGlass(), x, 1.5, front + 0.01, {
      cast: false,
    });
    g.add(glass);
    for (let i = 0; i <= 8; i++)
      g.add(box(0.05, 1.9, 0.05, M.black, x - 1.5 + i * 0.375, 1.5, front + 0.08, { cast: false }));
  }
  g.add(box(1.5, 2.4, 0.08, M.steel, 0, 1.2, front + 0.04, { cast: false }));
  g.add(mesh(new THREE.PlaneGeometry(1.3, 2.2), litStoreGlass(), 0, 1.15, front + 0.09, { cast: false }));
  const neon = signPlane(
    { w: 256, h: 96, bg: '#140808', fg: '#ff3048', lines: [{ text: 'PAWN', size: 76, font: 'Bangers' }] },
    1.8,
    0.7,
    2.5,
  );
  neon.position.set(-4, 1.9, front + 0.12);
  g.add(neon);
  const open = signPlane(
    { w: 256, h: 96, bg: '#081408', fg: '#39ff5a', lines: [{ text: 'OPEN', size: 72, font: 'Bangers' }] },
    1.2,
    0.45,
    2.5,
  );
  open.position.set(3.5, 1.9, front + 0.12);
  g.add(open);
  return g;
}

export function junkyardShack(): THREE.Group {
  const g = new THREE.Group();
  const metal = texMat(roofTexture(), { color: '#8f8a80', metal: 0.3, rough: 0.6 });
  g.add(mesh(worldBox(5, 3, 4, 1), metal, 0, 1.5, 0));
  g.add(box(5.6, 0.12, 4.6, mat('#6d4a33'), 0, 3.1, 0));
  g.add(box(2, 1.0, 0.1, mat('#1a1a1a'), 0, 1.6, 2.02, { cast: false }));
  g.add(box(2.2, 0.08, 0.6, texMat(woodTexture()), 0, 1.1, 2.25));
  const sign = signPlane(
    {
      w: 1024,
      h: 320,
      bg: '#f4e04d',
      fg: '#1b1b1b',
      border: '#1b1b1b',
      borderWidth: 14,
      gap: 6,
      lines: [
        { text: "JUNKYARD JERRY'S", size: 110, font: 'Rye' },
        { text: 'CASH FOR CANS • SCRAP • PARTS', size: 64, font: 'Anton' },
      ],
      grime: true,
    },
    4.6,
    1.45,
  );
  sign.position.set(0, 3.9, 2.02);
  g.add(sign);
  g.add(box(4.8, 1.55, 0.1, mat('#3a3a3a'), 0, 3.9, 1.95));
  g.add(box(1.8, 0.12, 1.8, M.steel, 2.8, 0.06, 3.2, { cast: false }));
  return g;
}

export function parkOffice(): THREE.Group {
  const g = buildTrailer({
    length: 8,
    width: 3.5,
    color: '#f2e8d0',
    stripe: '#b0417a',
    doorX: 0,
    windows: [
      { x: -2.6, side: 1, kind: 'blinds' },
      { x: 2.6, side: 1, kind: 'blinds' },
      { x: 0, side: -1, kind: 'dim' },
    ],
    skirting: 'lattice',
    steps: 'deck',
    porchLight: true,
    seed: 77,
  });
  const sign = signPlane(
    { w: 512, h: 128, bg: '#b0417a', fg: '#ffffff', lines: [{ text: 'OFFICE', size: 96, font: 'Rye' }] },
    2.4,
    0.6,
    1.0,
  );
  sign.position.set(0, 3.7, 1.0);
  g.add(sign);
  g.add(box(2.5, 0.7, 0.08, mat('#5a3a1e'), 0, 3.7, 0.95));
  const box2 = box(0.45, 0.6, 0.35, mat('#3b5b8c', { metal: 0.4 }), 1.9, 1.0, 2.9);
  g.add(box2);
  g.add(cyl(0.05, 0.05, 0.7, M.steel, 1.9, 0.35, 2.9, 6));
  return g;
}

/** Big Dave's display double-wide: the dream. */
export function doubleWide(): THREE.Group {
  const g = new THREE.Group();
  const L = 16;
  const D = 8;
  const H = 2.8;
  const base = 0.55;
  g.add(mesh(worldBox(L, H, D, 1), texMat(sidingTexture(), { color: '#e9dfc9' }), 0, base + H / 2, 0));
  g.add(box(L - 0.1, base, D - 0.1, mat('#cfc8b8'), 0, base / 2, 0, { cast: false }));
  const shape = new THREE.Shape();
  shape.moveTo(-D / 2 - 0.4, 0);
  shape.lineTo(D / 2 + 0.4, 0);
  shape.lineTo(0, 1.7);
  shape.closePath();
  const roofGeo = new THREE.ExtrudeGeometry(shape, { depth: L + 0.6, bevelEnabled: false });
  roofGeo.translate(0, 0, -(L + 0.6) / 2);
  const roof = mesh(roofGeo, mat('#5b4a43', { flat: true }), 0, base + H, 0);
  roof.rotation.y = Math.PI / 2;
  g.add(roof);
  for (const x of [-5.5, -2.5, 2.5, 5.5]) {
    g.add(box(1.4, 1.1, 0.06, mat('#ffffff'), x, base + 1.6, D / 2 + 0.01, { cast: false }));
    g.add(
      mesh(new THREE.PlaneGeometry(1.2, 0.95), litStoreGlass(), x, base + 1.6, D / 2 + 0.05, { cast: false }),
    );
    g.add(box(1.5, 0.25, 0.3, mat('#7a5530'), x, base + 0.9, D / 2 + 0.16));
    for (let i = 0; i < 4; i++)
      g.add(
        mesh(
          new THREE.SphereGeometry(0.09, 6, 4),
          mat(['#e63946', '#ffd166', '#f78fb3'][i % 3] as string),
          x - 0.5 + i * 0.33,
          base + 1.08,
          D / 2 + 0.2,
        ),
      );
  }
  g.add(box(1.0, 2.1, 0.07, mat('#8a2b22'), 0, base + 1.05, D / 2 + 0.03));
  const porch = mesh(worldBox(4.4, 0.12, 2.2, 1), texMat(woodTexture()), 0, base - 0.06, D / 2 + 1.1);
  g.add(porch);
  g.add(box(1.2, 0.3, 0.5, texMat(woodTexture()), 0, 0.15, D / 2 + 2.45));
  const tag = signPlane(
    {
      w: 512,
      h: 320,
      bg: '#ffffff',
      fg: '#c62828',
      border: '#c62828',
      borderWidth: 16,
      gap: 4,
      lines: [
        { text: 'MOVE-IN READY', size: 56, font: 'Anton', color: '#111' },
        { text: '$2,500', size: 150, font: 'Bangers' },
        { text: 'DOWN*', size: 48, font: 'Anton', color: '#111' },
      ],
    },
    1.5,
    0.95,
  );
  tag.position.set(2.3, base + 1.0, D / 2 + 2.1);
  g.add(tag);
  g.add(box(0.08, 1.0, 0.08, M.pole, 2.3, 0.5, D / 2 + 2.05));
  return g;
}

/** Wacky waving inflatable arm-flailing tube man. Returns segments for animation. */
export function tubeMan(color = '#e8312f'): {
  group: THREE.Group;
  segments: THREE.Group[];
  arms: THREE.Group[];
} {
  const g = new THREE.Group();
  g.add(cyl(0.45, 0.5, 0.6, mat('#222'), 0, 0.3, 0, 10));
  const m = mat(color, { rough: 0.5 });
  const segments: THREE.Group[] = [];
  let parent: THREE.Object3D = g;
  let y = 0.6;
  for (let i = 0; i < 6; i++) {
    const seg = new THREE.Group();
    seg.position.y = y;
    const r = 0.32 - i * 0.012;
    seg.add(cyl(r, r, 0.8, m, 0, 0.4, 0, 10));
    parent.add(seg);
    segments.push(seg);
    parent = seg;
    y = 0.8;
  }
  const head = new THREE.Group();
  head.position.y = 0.8;
  head.add(mesh(new THREE.SphereGeometry(0.3, 10, 8), m, 0, 0.2, 0));
  head.add(mesh(new THREE.SphereGeometry(0.07, 6, 4), mat('#ffffff'), -0.1, 0.28, 0.25));
  head.add(mesh(new THREE.SphereGeometry(0.07, 6, 4), mat('#ffffff'), 0.1, 0.28, 0.25));
  parent.add(head);
  const arms: THREE.Group[] = [];
  const armHost = segments[3] as THREE.Group;
  for (const s of [-1, 1]) {
    const arm = new THREE.Group();
    arm.position.set(s * 0.3, 0.6, 0);
    const tubeArm = cyl(0.12, 0.1, 1.4, m, 0, 0.7, 0, 8);
    arm.add(tubeArm);
    arm.rotation.z = -s * 1.2;
    armHost.add(arm);
    arms.push(arm);
  }
  g.traverse((o) => (o.userData.noBatch = true));
  return { group: g, segments, arms };
}
